import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { environment } from '../environments/environments';

@Injectable({
  providedIn: 'root',
})
export class SignalRWebsocketsService {
  #connection: HubConnection | null = null;
  #eventSubjects: { [key: string]: BehaviorSubject<any> } = {};

  /**
   * Establishes a connection to the SignalR Hub.
   * @param hubUrl The URL of the SignalR Hub.
   * @param accessToken The access token for authentication.
   * @returns An Observable that emits when the connection is established.
   */
  public connect(hubUrl: string, withCredentials: boolean): Observable<void> {
    const token = sessionStorage.getItem('access_token');

    if (!token && withCredentials === false) {
      return throwError(() => 'No access token found. Unable to connect to SignalR.');
    }

    this.#connection = new HubConnectionBuilder()
      .withUrl(`${environment.RESERVE_URL}/${hubUrl}`, {
        ...(!withCredentials && { accessTokenFactory: () => token! }),
        withCredentials,
      })
      .withAutomaticReconnect([0, 5, 15])
      .build();

    return from(this.#connection.start()).pipe(
      catchError((err) => {
        console.error('Error starting SignalR connection:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Ensures that the connection is in the 'Connected' state before invoking any method on the SignalR Hub.
   * @returns An Observable that emits when the connection is guaranteed.
   */
  private ensureConnection(): Observable<void> {
    if (this.#connection?.state === HubConnectionState.Connected) {
      return from(Promise.resolve());
    } else if (
      this.#connection?.state === HubConnectionState.Connecting ||
      this.#connection?.state === HubConnectionState.Reconnecting
    ) {
      return from(this.#connection.start()).pipe(take(1));
    } else {
      return throwError(() => 'Connection not established. Call connect() first.');
    }
  }

  /**
   * Listens to a specific event from the SignalR Hub.
   * @param eventName The name of the event to listen to.
   * @returns An Observable that emits when the event is received.
   */
  public listenToEvent<T>(eventName: string): Observable<T> {
    if (!this.#eventSubjects[eventName]) {
      this.#eventSubjects[eventName] = new BehaviorSubject<T | null>(null);
      this.#connection?.on(eventName, (data: T) => {
        this.#eventSubjects[eventName].next(data);
      });
    }
    return this.#eventSubjects[eventName].asObservable();
  }

  public removeEventListening(eventName: string): void {
    this.#eventSubjects[eventName]?.unsubscribe();
  }

  /**
   * Invokes a method on the SignalR Hub.
   * @param methodName The name of the method to invoke.
   * @param args The arguments to pass to the method.
   * @returns An Observable that emits when the method is invoked.
   */
  public invoke(methodName: string, ...args: any[]): Observable<any> {
    return this.ensureConnection().pipe(
      switchMap(() => {
        return from(this.#connection!.invoke(methodName, ...args));
      }),
      catchError((err) => {
        console.error(`Error invoking method ${methodName}:`, err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Disconnects the SignalR connection.
   */
  public disconnect(): void {
    if (this.#connection) {
      this.#connection.stop().catch((err) => {
        console.error('Error stopping SignalR connection:', err);
      });
    }
  }
}
